import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, SearchOutlined, SettingOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Checkbox, Col, DatePicker, Dropdown, Form, Input, InputNumber, message, Modal, Popconfirm, Progress, Row, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { transportApi } from "../../api/transportApi";
import type { BiltyNumberSeriesIssueDto, DocumentNumberInventoryDto, DocumentSequenceNumbersDto } from "../../api/transportApi";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { BranchCreateModal } from "../../shared/components/BranchCreateModal";
import type { LookupItem } from "../../shared/components/LookupModal";
import { getApiErrorMessage } from "../../utils/apiError";
import { getEnteredByUser } from "../../utils/currentUser";
import { HeadOfficeStationeryReceiptPanel } from "./HeadOfficeStationeryReceiptPanel";

type FormValues = {
  documentType: "Consignment" | "Manifest" | "GatePass";
  issueBranchId: number;
  assignedBranchId: number;
  issueDate: dayjs.Dayjs;
  challanNo?: string;
  itemCode?: string;
  itemName?: string;
  quantity: number;
  unitType: string;
  numbersPerBook: number;
  startNumber: number;
  endNumber?: number;
  remarks?: string;
};

const calcEndNumber = (start?: number, quantity?: number, numbersPerBook?: number) => {
  if (!start || start <= 0) return undefined;
  const qty = quantity && quantity > 0 ? quantity : 1;
  const perBook = numbersPerBook && numbersPerBook > 0 ? numbersPerBook : 50;
  return start + qty * perBook - 1;
};

const stationeryColumnOptions = [
  { key: "issueDate", label: "Issue Date" },
  { key: "challanNo", label: "Challan" },
  { key: "issueBranchName", label: "Issued By Branch / Station" },
  { key: "assignedBranchName", label: "Assigned Branch / Station" },
  { key: "itemName", label: "Item" },
  { key: "quantity", label: "Quantity" },
  { key: "startNumber", label: "Start Number" },
  { key: "endNumber", label: "End Number" },
  { key: "isActive", label: "Status" }
] as const;

const stationeryColumnsStorageKey = "transporterp:bilty-stationery:visible-columns:v1";

const getInitialStationeryColumns = () => {
  const defaults = stationeryColumnOptions.map((item) => item.key);
  if (typeof window === "undefined") return defaults;
  try {
    const saved = JSON.parse(window.localStorage.getItem(stationeryColumnsStorageKey) ?? "null");
    if (!Array.isArray(saved)) return defaults;
    return saved.filter((key): key is string => stationeryColumnOptions.some((item) => item.key === key));
  } catch {
    return defaults;
  }
};

export const BiltyStationeryPage = () => {
  const navigate = useNavigate();
  const sessionUser = useAuthStore((state) => state.user);
  const sessionBranchId = useAuthStore((state) => state.branchId);
  const isAdmin = sessionUser?.roles.some((role) => ["admin", "administrator", "superadmin"].includes(role.toLowerCase())) ?? false;
  const lockedBranchId = !isAdmin && sessionBranchId && /^\d+$/.test(sessionBranchId) ? Number(sessionBranchId) : undefined;
  const [rows, setRows] = useState<BiltyNumberSeriesIssueDto[]>([]);
  const [inventory, setInventory] = useState<DocumentNumberInventoryDto[]>([]);
  const [headOffice, setHeadOffice] = useState<{ id: number; branchCode: string; branchName: string } | null>(null);
  const [headOfficeError, setHeadOfficeError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [documentType, setDocumentType] = useState<"Consignment" | "Manifest" | "GatePass">("Consignment");
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(lockedBranchId);
  const [branchFilterOptions, setBranchFilterOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(getInitialStationeryColumns);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [workflowTab, setWorkflowTab] = useState<"branch" | "ho">(isAdmin ? "ho" : "branch");
  const [form] = Form.useForm<FormValues>();
  const [detailDocumentType, setDetailDocumentType] = useState<"Consignment" | "Manifest" | "GatePass" | null>(null);
  const [detailBranchId, setDetailBranchId] = useState<number | undefined>(lockedBranchId ?? selectedBranchId);
  const [detailLedger, setDetailLedger] = useState<DocumentSequenceNumbersDto | null>(null);
  const [detailStatus, setDetailStatus] = useState<"all" | "used" | "pending">("all");
  const [detailSearch, setDetailSearch] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [detailLoading, setDetailLoading] = useState(false);
  const [headOfficeSetupOpen, setHeadOfficeSetupOpen] = useState(false);
  const [headOfficeSaving, setHeadOfficeSaving] = useState(false);
  const [headOfficeForm] = Form.useForm<{ branchId: number }>();

  // Conflict warning state
  const [conflictModal, setConflictModal] = useState<{
    conflict: BiltyNumberSeriesIssueDto;
    pendingPayload: any;
  } | null>(null);

  const branchFetcher = useCallback(
    (q: string, page: number, pageSize: number) => transportApi.searchBranchLookups(q, page, pageSize),
    []
  );

  // Inline branch create
  type BranchTarget = "issueBranchId" | "assignedBranchId";
  const [branchCreateOpen, setBranchCreateOpen] = useState(false);
  const [branchPrefill, setBranchPrefill]       = useState("");
  const [branchTarget, setBranchTarget]         = useState<BranchTarget>("issueBranchId");
  const [branchSeeds, setBranchSeeds]           = useState<LookupItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, summary] = await Promise.all([
        transportApi.listBiltyNumberSeries({
          page: 1, pageSize: 200, q: search.trim() || undefined,
          branchId: lockedBranchId ?? selectedBranchId,
          documentType, sort: "id", order: "DESC"
        }),
        transportApi.getDocumentNumberInventory(lockedBranchId ?? selectedBranchId)
      ]);
      setRows(result.data);
      setInventory(summary);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load Bilty stationery ranges"));
    } finally {
      setLoading(false);
    }
  }, [search, documentType, lockedBranchId, selectedBranchId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    transportApi.getHeadOffice()
      .then((branch) => { setHeadOffice(branch); setHeadOfficeError(undefined); })
      .catch((error) => { setHeadOffice(null); setHeadOfficeError(getApiErrorMessage(error, "Head Office is not configured")); });
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    transportApi.searchBranchLookups("", 1, 200)
      .then((result) => setBranchFilterOptions(result.items.map((item) => ({ value: item.id, label: item.label }))))
      .catch(() => setBranchFilterOptions([]));
  }, [isAdmin]);

  useEffect(() => {
    window.localStorage.setItem(stationeryColumnsStorageKey, JSON.stringify(visibleColumnKeys));
  }, [visibleColumnKeys]);

  const saveHeadOffice = async () => {
    const { branchId } = await headOfficeForm.validateFields();
    setHeadOfficeSaving(true);
    try {
      const branch = await transportApi.setHeadOffice(branchId);
      setHeadOffice(branch);
      setHeadOfficeError(undefined);
      form.setFieldValue("issueBranchId", branch.id);
      setHeadOfficeSetupOpen(false);
      headOfficeForm.resetFields();
      message.success(`${branch.branchName} is now the Head Office`);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to set Head Office"));
    } finally {
      setHeadOfficeSaving(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      documentType,
      issueBranchId: headOffice?.id,
      issueDate: dayjs(),
      itemCode: documentType === "Manifest" ? "MANIFEST" : documentType === "GatePass" ? "GATEPASS" : "A0092",
      itemName: documentType === "Manifest" ? "MANIFEST BOOK" : documentType === "GatePass" ? "GATE PASS BOOK" : "CONSIGNMENT BOOK",
      quantity: 1,
      unitType: "BOOK",
      numbersPerBook: 50
    });
    setDrawerOpen(true);
  };

  const openEdit = (row: BiltyNumberSeriesIssueDto) => {
    setEditingId(row.id);
    form.resetFields();
    form.setFieldsValue({
      documentType: row.documentType,
      issueBranchId: headOffice?.id ?? row.issueBranchId,
      assignedBranchId: row.assignedBranchId,
      issueDate: dayjs(row.issueDate),
      challanNo: row.challanNo ?? undefined,
      itemCode: row.itemCode,
      itemName: row.itemName,
      quantity: row.quantity,
      unitType: row.unitType,
      numbersPerBook: row.numbersPerBook,
      startNumber: row.startNumber,
      endNumber: row.endNumber,
      remarks: row.remarks ?? undefined,
    });
    setDrawerOpen(true);
  };

  const onValuesChange = (changed: Partial<FormValues>, values: FormValues) => {
    if (changed.documentType) {
      setDocumentType(changed.documentType);
      form.setFieldsValue({
        itemCode: changed.documentType === "Manifest" ? "MANIFEST" : changed.documentType === "GatePass" ? "GATEPASS" : "A0092",
        itemName: changed.documentType === "Manifest" ? "MANIFEST BOOK" : changed.documentType === "GatePass" ? "GATE PASS BOOK" : "CONSIGNMENT BOOK",
      });
    }
    const nextEnd = calcEndNumber(values.startNumber, values.quantity, values.numbersPerBook);
    if (nextEnd !== values.endNumber) {
      form.setFieldValue("endNumber", nextEnd);
    }
  };

  /** Returns the first conflicting range if the given number range overlaps another active range
   *  for the same document type, ignoring the currently editing record. */
  const findConflict = (
    startNumber: number,
    endNumber: number,
    rangeDocumentType: string,
    excludeId: number | null
  ): BiltyNumberSeriesIssueDto | null => {
    for (const row of rows) {
      if (row.id === excludeId) continue;
      if (!row.isActive) continue;
      if (row.documentType !== rangeDocumentType) continue;
      if (startNumber <= row.endNumber && endNumber >= row.startNumber) {
        return row;
      }
    }
    return null;
  };

  const buildPayload = (values: FormValues) => ({
    documentType: values.documentType,
    issueBranchId: values.issueBranchId,
    assignedBranchId: values.assignedBranchId,
    issueDate: values.issueDate.toISOString(),
    challanNo: values.challanNo ?? "",
    itemCode: values.itemCode ?? "A0092",
    itemName: values.itemName ?? "CONSIGNMENT BOOK",
    quantity: values.quantity,
    unitType: values.unitType ?? "BOOK",
    numbersPerBook: values.numbersPerBook,
    startNumber: values.startNumber,
    enteredBy: getEnteredByUser(),
    remarks: values.remarks ?? null,
  });

  const save = async () => {
    const values = await form.validateFields();
    const endNumber = calcEndNumber(values.startNumber, values.quantity, values.numbersPerBook) ?? values.startNumber;

    // Conflict check
    const conflict = findConflict(values.startNumber, endNumber, values.documentType, editingId);
    if (conflict) {
      setConflictModal({ conflict, pendingPayload: buildPayload(values) });
      return;
    }

    await persist(buildPayload(values));
  };

  const persist = async (payload: any) => {
    setConflictModal(null);
    setSaving(true);
    try {
      if (editingId) {
        await transportApi.updateBiltyNumberSeries(editingId, payload);
        message.success("Document number range updated");
      } else {
        await transportApi.createBiltyNumberSeries(payload);
        message.success("Document number range issued");
      }
      setDrawerOpen(false);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, editingId ? "Unable to update range" : "Unable to issue document number range"));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      await transportApi.deactivateBiltyNumberSeries(id);
      message.success("Range deactivated");
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to deactivate range"));
    }
  };

  const editingRow = editingId ? rows.find(r => r.id === editingId) : null;

  const loadDocumentNumbers = useCallback(async (
    type: "Consignment" | "Manifest" | "GatePass",
    status: "all" | "used" | "pending",
    q: string,
    page: number,
    branchOverride?: number | null
  ) => {
    setDetailLoading(true);
    try {
      const branchId = lockedBranchId ?? (branchOverride !== undefined ? branchOverride ?? undefined : detailBranchId ?? selectedBranchId);
      let result: DocumentSequenceNumbersDto | null = null;
      try {
        result = await transportApi.getDocumentSequenceNumbersByType(type, {
          branchId,
          status,
          q: q.trim() || undefined,
          page,
          pageSize: 100
        });
      } catch {
        // Older running API instances may not yet expose the aggregate endpoint.
      }

      if (!result || result.total === 0) {
        const ranges = await transportApi.listBiltyNumberSeries({
          page: 1,
          pageSize: 200,
          branchId,
          documentType: type,
          sort: "startNumber",
          order: "ASC"
        });
        if (ranges.data.length > 0) {
          const ledgers = await Promise.all(ranges.data.map(async (range) => {
            try {
              return await transportApi.getDocumentSequenceNumbers(range.id, {
                status,
                q: q.trim() || undefined,
                page: 1,
                pageSize: 500
              });
            } catch (error) {
              if (range.usedCount > 0) throw error;
              const exact = q.trim() ? Number(q.trim()) : undefined;
              const allNumbers = Array.from(
                { length: range.totalNumbers },
                (_, index) => range.startNumber + index
              ).filter((number) => !exact || number === exact);
              const items = status === "used" ? [] : allNumbers.map((number) => ({
                number,
                status: "Pending" as const,
                issueId: range.id,
                assignedBranchId: range.assignedBranchId,
                assignedBranchName: range.assignedBranchName
              }));
              return {
                issueId: range.id,
                documentType: type,
                assignedBranchName: range.assignedBranchName,
                startNumber: range.startNumber,
                endNumber: range.endNumber,
                status,
                page: 1,
                pageSize: 500,
                total: items.length,
                items
              } satisfies DocumentSequenceNumbersDto;
            }
          }));
          const allItems = ledgers.flatMap((ledger) => ledger.items).sort((left, right) => left.number - right.number);
          const offset = (page - 1) * 100;
          result = {
            issueId: 0,
            documentType: type,
            assignedBranchName: "",
            startNumber: allItems[0]?.number ?? 0,
            endNumber: allItems[allItems.length - 1]?.number ?? 0,
            status,
            page,
            pageSize: 100,
            total: ledgers.reduce((total, ledger) => total + ledger.total, 0),
            items: allItems.slice(offset, offset + 100)
          };
        }
      }

      setDetailLedger(result);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load sequence numbers"));
    } finally {
      setDetailLoading(false);
    }
  }, [detailBranchId, lockedBranchId, selectedBranchId]);

  const openDocumentDetails = (type: "Consignment" | "Manifest" | "GatePass") => {
    const branchId = lockedBranchId ?? selectedBranchId;
    setDetailDocumentType(type);
    setDetailBranchId(branchId);
    setDetailStatus("all");
    setDetailSearch("");
    setDetailPage(1);
    setDetailLedger(null);
    void loadDocumentNumbers(type, "all", "", 1, branchId);
  };

  const openUsedDocument = (item: DocumentSequenceNumbersDto["items"][number]) => {
    if (!detailDocumentType || item.status !== "Used" || !item.documentId) return;
    if (detailDocumentType === "Consignment") {
      navigate(`/consignments?consignmentId=${item.documentId}`);
    } else if (detailDocumentType === "Manifest") {
      navigate(`/manifests?manifestId=${item.documentId}`);
    } else if (item.parentDocumentId) {
      navigate(`/manifests?manifestId=${item.parentDocumentId}&gatePassId=${item.documentId}`);
    }
  };

  type SequenceItem = DocumentSequenceNumbersDto["items"][number];
  type SequenceRangeRow = {
    key: string;
    isRange: true;
    rangeStart: number;
    rangeEnd: number;
    assignedBranchName: string;
    children: SequenceItem[];
  };

  const groupedDetailRows = useMemo<SequenceRangeRow[]>(() => {
    const groups = new Map<string, SequenceRangeRow>();
    for (const item of detailLedger?.items ?? []) {
      const rangeStart = Math.floor((item.number - 1) / 50) * 50 + 1;
      const rangeEnd = rangeStart + 49;
      const key = `${item.assignedBranchId}-${item.issueId}-${rangeStart}`;
      const group = groups.get(key) ?? {
        key,
        isRange: true as const,
        rangeStart,
        rangeEnd,
        assignedBranchName: item.assignedBranchName,
        children: []
      };
      group.children.push(item);
      groups.set(key, group);
    }
    return Array.from(groups.values()).sort((left, right) => left.rangeStart - right.rangeStart);
  }, [detailLedger]);

  const columns = useMemo<ColumnsType<BiltyNumberSeriesIssueDto>>(() => {
    const allColumns: ColumnsType<BiltyNumberSeriesIssueDto> = [
    {
      key: "issueDate",
      title: "Issue Date",
      dataIndex: "issueDate",
      width: 110,
      render: (value: string) => dayjs(value).format("DD/MM/YYYY")
    },
    { key: "challanNo", title: "Challan", dataIndex: "challanNo", width: 120, render: (value: string) => value || "-" },
    { key: "issueBranchName", title: "Issued By Branch / Station", dataIndex: "issueBranchName", width: 210 },
    { key: "assignedBranchName", title: "Assigned Branch / Station", dataIndex: "assignedBranchName", width: 210 },
    { key: "itemName", title: "Item", dataIndex: "itemName", ellipsis: true },
    { key: "quantity", title: "Qty", dataIndex: "quantity", width: 70, align: "right" },
    { key: "startNumber", title: "Start #", dataIndex: "startNumber", width: 120, align: "right" },
    { key: "endNumber", title: "End #", dataIndex: "endNumber", width: 120, align: "right" },
    {
      key: "isActive",
      title: "Status",
      dataIndex: "isActive",
      width: 90,
      render: (value: boolean) => <Tag color={value ? "green" : "default"}>{value ? "Active" : "Inactive"}</Tag>
    },
    {
      key: "actions",
      title: "",
      width: 105,
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
          />
          {row.isActive ? (
            <Popconfirm title="Deactivate this range?" onConfirm={() => void deactivate(row.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : null}
        </Space>
      )
    }];
    return allColumns.filter((column) => column.key !== "actions" && visibleColumnKeys.includes(String(column.key)));
  }, [rows, visibleColumnKeys]);

  return (
    <Card
      className="stationery-page-card"
      title={
        <Space size={8}>
          <span>Document Stationery Inventory</span>
          <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>D</kbd></span>
        </Space>
      }
      extra={
        <Space>
          {isAdmin ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedBranchId}
              placeholder="All Branches / Stations"
              options={branchFilterOptions}
              onChange={(value) => {
                setLoading(true);
                setSelectedBranchId(value);
                setDetailDocumentType(null);
              }}
              style={{ width: 230 }}
            />
          ) : null}
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search range / branch"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onPressEnter={() => void load()}
            style={{ width: 260 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void load()} />
        </Space>
      }
    >
      <Tabs activeKey={workflowTab} onChange={(key) => setWorkflowTab(key as "branch" | "ho")} items={[
        ...(isAdmin ? [{ key: "ho", label: "HO Printer Inventory & Assignment" }] : []),
        { key: "branch", label: isAdmin ? "Issued Inventories" : "My Assigned Stationery" }
      ]} />
      {workflowTab === "ho" ? <HeadOfficeStationeryReceiptPanel /> : <>
      <Spin spinning={loading} tip="Refreshing branch data..." size="large">
        <div className="sequence-inventory-grid" aria-busy={loading}>
          {inventory.map((item) => {
            const percent = item.assignedCount > 0 ? Math.round((item.usedCount / item.assignedCount) * 100) : 0;
            const label = item.documentType === "Consignment" ? "Bilty / LR" : item.documentType === "GatePass" ? "Gate Pass" : item.documentType;
            return <section className="sequence-inventory-item" key={item.documentType}>
              <div className="sequence-inventory-heading">
                <Typography.Text strong>{label}</Typography.Text>
                <Tooltip placement="bottom" title={`${item.historicalRangeCount} old/deactivated ranges: ${item.historicalAssignedCount} assigned, ${item.historicalUsedCount} used`}>
                  <Tag>{item.activeRangeCount} active / {item.historicalRangeCount} old</Tag>
                </Tooltip>
              </div>
              <Button
                className="sequence-view-details"
                icon={<EyeOutlined />}
                onClick={() => openDocumentDetails(item.documentType)}
                block
              >
                View Details
              </Button>
              <div className="sequence-inventory-counts">
              <span><b>{item.assignedCount}</b> Total Numbers</span>
              <span><b>{item.usedCount}</b> Used</span>
              <span className="pending"><b>{item.pendingCount}</b> Available</span>
              </div>
              <Progress percent={percent} size="small" showInfo={false} />
              <Typography.Text type="secondary" className="sequence-branch-summary">
                {item.branches.length ? item.branches.map((branch) => `${branch.branchName}: ${branch.pendingCount} available`).join(" | ") : "No ranges reserved for a Branch / Station"}
              </Typography.Text>
            </section>;
          })}
        </div>
      </Spin>

      {headOfficeError ? (
        <Alert
          type="error"
          showIcon
          message={headOfficeError}
          action={<Button size="small" type="primary" onClick={() => setHeadOfficeSetupOpen(true)}>Set Head Office</Button>}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Tabs
        className="stationery-document-tabs"
        type="card"
        size="small"
        activeKey={documentType}
        onChange={(key) => setDocumentType(key as "Consignment" | "Manifest" | "GatePass")}
        items={[
          { key: "Consignment", label: "Bilty / LR" },
          { key: "Manifest", label: "Manifest" },
          { key: "GatePass", label: "Gate Pass" }
        ]}
        tabBarExtraContent={
          <Dropdown
            trigger={["click"]}
            dropdownRender={() => (
              <div className="stationery-column-picker" onClick={(event) => event.stopPropagation()}>
                <Typography.Text strong>Show columns</Typography.Text>
                {stationeryColumnOptions.map((option) => (
                  <Checkbox
                    key={option.key}
                    checked={visibleColumnKeys.includes(option.key)}
                    onChange={(event) => setVisibleColumnKeys((current) => event.target.checked
                      ? [...current, option.key]
                      : current.filter((key) => key !== option.key))}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </div>
            )}
          >
            <Button icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        }
      />

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 25, showSizeChanger: true }}
      />
      </>}

      <Modal
        rootClassName="static-overlay-modal"
        title={
          <div>
            <div>{`${detailDocumentType === "Consignment" ? "Bilty / LR" : detailDocumentType === "GatePass" ? "Gate Pass" : detailDocumentType ?? "Document"} Sequence Numbers`}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              {branchFilterOptions.find((option) => option.value === detailBranchId)?.label
                ?? detailLedger?.items[0]?.assignedBranchName
                ?? (detailBranchId ? `Branch / Station #${detailBranchId}` : "All Branches / Stations")}
            </Typography.Text>
          </div>
        }
        open={!!detailDocumentType}
        width={820}
        footer={<Button onClick={() => setDetailDocumentType(null)}>Close</Button>}
        onCancel={() => setDetailDocumentType(null)}
        destroyOnHidden
      >
        <Space wrap style={{ marginBottom: 16 }}>
          {isAdmin ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={detailBranchId}
              placeholder="All Branches / Stations"
              options={branchFilterOptions}
              onChange={(value) => {
                if (!detailDocumentType) return;
                setDetailBranchId(value);
                setDetailPage(1);
                setDetailLedger(null);
                void loadDocumentNumbers(detailDocumentType, detailStatus, detailSearch, 1, value ?? null);
              }}
              style={{ width: 230 }}
            />
          ) : null}
          <Select
            value={detailStatus}
            style={{ width: 160 }}
            options={[
              { value: "all", label: "All numbers" },
              { value: "used", label: "Used by document" },
              { value: "pending", label: "Available" }
            ]}
            onChange={(value) => {
              if (!detailDocumentType) return;
              setDetailStatus(value);
              setDetailPage(1);
              void loadDocumentNumbers(detailDocumentType, value, detailSearch, 1);
            }}
          />
          <Input.Search
            allowClear
            value={detailSearch}
            placeholder="Search exact sequence number"
            style={{ width: 280 }}
            onChange={(event) => setDetailSearch(event.target.value)}
            onSearch={(value) => {
              if (!detailDocumentType) return;
              setDetailSearch(value);
              setDetailPage(1);
              void loadDocumentNumbers(detailDocumentType, detailStatus, value, 1);
            }}
          />
        </Space>
        <Table<SequenceRangeRow | SequenceItem>
          rowKey={(item) => "isRange" in item ? item.key : `${item.issueId}-${item.number}`}
          size="small"
          loading={detailLoading}
          dataSource={groupedDetailRows}
          expandable={{
            expandRowByClick: true,
            indentSize: 24,
            rowExpandable: (item) => "isRange" in item
          }}
          columns={[
            ...(!detailBranchId ? [{ title: "Branch / Station", dataIndex: "assignedBranchName", ellipsis: true, render: (value: string, item: SequenceRangeRow | SequenceItem) => "isRange" in item ? <Typography.Text strong>{value}</Typography.Text> : value }] : []),
            {
              title: "Sequence Range / Number",
              width: 230,
              render: (_: unknown, item: SequenceRangeRow | SequenceItem) => "isRange" in item
                ? <Space><Typography.Text strong>{item.rangeStart}–{item.rangeEnd}</Typography.Text><Typography.Text type="secondary">({item.children.length} numbers)</Typography.Text></Space>
                : <Typography.Text>{item.number}</Typography.Text>
            },
            { title: "State", width: 160, render: (_: unknown, item: SequenceRangeRow | SequenceItem) => {
              if ("isRange" in item) {
                const assigned = item.children.filter((child) => child.status === "Used").length;
                return <Space size={4}><Tag color="green">{assigned} Assigned</Tag><Tag color="gold">{item.children.length - assigned} Available</Tag></Space>;
              }
              return <Tag color={item.status === "Used" ? "green" : "gold"}>{item.status === "Used" ? "Assigned" : "Available"}</Tag>;
            } },
            {
              title: "",
              width: 120,
              render: (_: unknown, item: SequenceRangeRow | SequenceItem) => !("isRange" in item) && item.status === "Used" && item.documentId
                ? <Button size="small" onClick={(event) => { event.stopPropagation(); openUsedDocument(item); }}>View</Button>
                : null
            }
          ]}
          pagination={{
            current: detailPage,
            pageSize: detailLedger?.pageSize ?? 100,
            total: detailLedger?.total ?? 0,
            showSizeChanger: false,
            showTotal: (total) => `${total} sequence number${total === 1 ? "" : "s"}`,
            onChange: (page) => {
              if (!detailDocumentType) return;
              setDetailPage(page);
              void loadDocumentNumbers(detailDocumentType, detailStatus, detailSearch, page);
            }
          }}
          locale={{ emptyText: "No sequence numbers match this filter" }}
        />
      </Modal>

      {/* Conflict warning modal */}
      <Modal
        open={!!conflictModal}
        title={<Space><WarningOutlined style={{ color: "#faad14" }} />Number Range Conflict Detected</Space>}
        onCancel={() => setConflictModal(null)}
        footer={[
          <Button key="cancel" type="primary" onClick={() => setConflictModal(null)}>Return and Fix Range</Button>,
        ]}
      >
        {conflictModal && (
          <>
            <p>The number range overlaps an existing active range for this document type:</p>
            <Alert
              type="warning"
              showIcon
              message={`Range ${conflictModal.conflict.startNumber}–${conflictModal.conflict.endNumber}`}
              description={`Assigned to: ${conflictModal.conflict.assignedBranchName}  ·  Challan: ${conflictModal.conflict.challanNo || "—"}  ·  Usage: ${conflictModal.conflict.usedCount}/${conflictModal.conflict.totalNumbers}`}
              style={{ marginTop: 8, marginBottom: 8 }}
            />
            <p>Overlapping ranges are not permitted. Correct the start number or deactivate the conflicting range.</p>
          </>
        )}
      </Modal>

      <Modal
        rootClassName="static-overlay-modal"
        title="Set Head Office"
        open={headOfficeSetupOpen}
        okText="Set Head Office"
        confirmLoading={headOfficeSaving}
        onOk={() => void saveHeadOffice()}
        onCancel={() => {
          setHeadOfficeSetupOpen(false);
          headOfficeForm.resetFields();
        }}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message="The selected Branch / Station will issue all stationery number ranges."
        />
        <Form form={headOfficeForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="branchId"
            label="Head Office Branch / Station"
            rules={[{ required: true, message: "Select the Head Office Branch / Station" }]}
          >
            <LookupSelect
              fetcher={branchFetcher}
              placeholder="Search branch / station..."
              seededOptions={branchSeeds}
            />
          </Form.Item>
        </Form>
      </Modal>

      <StaticDrawer
        title={editingId ? "Edit Document Number Range" : "Issue Document Number Range"}
        width={720}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingId(null); }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => { setDrawerOpen(false); setEditingId(null); }}>Cancel</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()}>
              {editingId ? "Update" : "Save"}
            </Button>
          </div>
        }
        destroyOnHidden
        maskClosable={false}
        keyboard={false}
      >
        <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
          {/* Warning when editing a range that is partially used */}
          {editingId && editingRow && editingRow.usedCount > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${editingRow.usedCount} document number(s) from this range are already in use.`}
              description="Used ranges are locked by the server. Deactivate this range and issue a new range when changes are required."
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item name="documentType" label="Document Type" rules={[{ required: true }]}>
            <Select disabled={!!editingId && !!editingRow && editingRow.usedCount > 0} options={[
              { value: "Consignment", label: "Bilty / LR (Consignment)" },
              { value: "Manifest", label: "Manifest" },
              { value: "GatePass", label: "Gate Pass" },
            ]} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="issueBranchId" hidden rules={[{ required: true }]}><Input /></Form.Item>
              <Form.Item label="Issuing Branch / Station" required extra="Fixed to the configured Head Office">
                <Input value={headOffice ? `${headOffice.branchCode} - ${headOffice.branchName}` : "Head Office not configured"} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="assignedBranchId" label="Assigned Branch / Station" rules={[{ required: true, message: "Select branch / station" }]}>
                <LookupSelect fetcher={branchFetcher} placeholder="Search branch / station..."
                  addNewLabel="Branch / Station" seededOptions={branchSeeds}
                  onAddNew={(q) => { setBranchPrefill(q); setBranchTarget("assignedBranchId"); setBranchCreateOpen(true); }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="issueDate" label="Issue Date" rules={[{ required: true, message: "Select issue date" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="challanNo" label="Challan #">
                <Input maxLength={30} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unitType" label="Unit Type" rules={[{ required: true, message: "Enter unit type" }]}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="itemCode" label="Item Code" rules={[{ required: true, message: "Enter item code" }]}>
                <Input maxLength={30} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="itemName" label="Item Name" rules={[{ required: true, message: "Enter item name" }]}>
                <Input maxLength={150} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="quantity" label="Qty" rules={[{ required: true, message: "Enter quantity" }]}>
                <InputNumber style={{ width: "100%" }} min={1} precision={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="numbersPerBook" label="Per Book" rules={[{ required: true, message: "Enter count" }]}>
                <InputNumber style={{ width: "100%" }} min={1} precision={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="startNumber" label="Start #" rules={[{ required: true, message: "Enter start number" }]}>
                <InputNumber style={{ width: "100%" }} min={1} precision={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="endNumber" label="End # (auto)">
                <InputNumber style={{ width: "100%" }} disabled precision={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>

        </Form>
      </StaticDrawer>

      <BranchCreateModal
        open={branchCreateOpen}
        prefillName={branchPrefill}
        onClose={() => setBranchCreateOpen(false)}
        onCreated={(item) => {
          setBranchSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue(branchTarget, item.id);
          setBranchCreateOpen(false);
        }}
      />
    </Card>
  );
};
